import { addDays } from 'date-fns'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { rem } from 'polished'
import queryString from 'query-string'
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useLocation } from 'react-use'
import { SESSION_SUBSCRIPTION_DATE_RANGE } from 'shared-constants'
import { BOOKING_STEP } from 'components/BookingModal/constants'
import { ExpertReviews } from 'components/ExpertReviews'
import { GET_NOTIFIED_STEP } from 'components/GetNotifiedModal/constants'
import { InfoCard } from 'components/InfoCard'
import { CONTAINER_HORIZONTAL_PADDING, Flex, Grid } from 'components/Layout'
import {
  SessionBookingCardListing,
  SessionBookingCardListingMobile,
} from 'components/SessionBookingCardListing'
import { SIGNED_IN_AS_USER_HEADER_DESKTOP_HEIGHT } from 'components/SignedInAsUserHeader'
import { HEADER_HEIGHT_DESKTOP } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { useBookingModalAvailabilitiesQuery } from 'hooks'
import {
  GA4_ECOM_CUSTOM_EVENTS,
  GA4_EVENTS,
  GA4_SIGN_IN_AREAS,
  useGoogleAnalytics,
} from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { SPACE } from 'Theme'
import {
  useFeaturedExpertsLazyQuery,
  type CalculatedPrice,
  type PublicExpertDetailConsultationTabQuery,
  useClientSubscribeToExpertAvailabilitiesMutation,
} from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { trackAbandonedBookingCheckout } from 'utils/bing'
import { klaviyoIdentifyByEmail } from 'utils/klaviyo'
import { silentPushUrl } from 'utils/pushUrl'
import { AboutSection } from './AboutSection'
import { ExpertExpertises } from './ExpertExpertises'
import ExpertTestimonialsCarousel from './ExpertTestimonialsCarousel'
import { FAQSection } from './FAQSection'
import {
  usePublicExpertDetailConsultationTab,
  useExpertDetailParseDataFromUrlHook,
  usePreselectAvailableTime,
} from './hooks'
import { ParticipateSection } from './ParticipateSection'
import RecommendExpertModal from './RecommendExpertModal'

const GetNotifiedModal = dynamic(() => import('components/GetNotifiedModal'), {
  ssr: false,
})

const BookingModal = dynamic(() => import('components/BookingModal'), {
  ssr: false,
})

const ASIDE_MAX_WIDTH = {
  TABLET: rem(340),
  DESKTOP: rem(360),
}

interface ConsultationsTabContentProps {
  slug: string
}

const getSimilarSessionFilters = ({
  applyPrice = true,
  applyAvailability = true,
  applyLength = true,
  applyTags = true,
  dateRange,
  selectedSession,
  expertData,
}) => {
  const price = {
    from: Math.floor(selectedSession.price.total * 0.8),
    to: Math.floor(selectedSession.price.total * 1.2),
  }
  const tags = expertData.tags.map(({ title }) => title)
  const today = new Date()
  let availability
  switch (true) {
    case dateRange.includes(SESSION_SUBSCRIPTION_DATE_RANGE.NEXT_SIX_MONTHS):
      availability = {
        from: new Date().toISOString(),
        to: addDays(new Date(), 180).toISOString(),
      }
      break
    case dateRange.includes(SESSION_SUBSCRIPTION_DATE_RANGE.NEXT_MONTH):
      availability = {
        from: new Date().toISOString(),
        to: addDays(new Date(), 30).toISOString(),
      }
      break
    case dateRange.includes(SESSION_SUBSCRIPTION_DATE_RANGE.NEXT_TWO_WEEKS):
      availability = {
        from: new Date().toISOString(),
        to: addDays(new Date(), 14).toISOString(),
      }
      break
    case dateRange.includes(SESSION_SUBSCRIPTION_DATE_RANGE.THIS_WEEK):
    default:
      availability = {
        from: today.toISOString(),
        to: addDays(new Date(), 7).toISOString(),
      }
      break
  }

  const sessionLength = [selectedSession.lengthInMinutes]
  return {
    ...(applyPrice && { price }),
    ...(applyLength && { sessionLength }),
    ...(applyAvailability && { availability }),
    ...(applyTags && { tags }),
  }
}

const hasResponseSomeResults = ({ data: { publicExperts } }) =>
  publicExperts.data.length > 0

export const ConsultationsTabContent: React.FC<
  ConsultationsTabContentProps
> = ({ slug }) => {
  const eventFired = useRef(false)
  const { executeDataToDataLayer } = useGoogleAnalytics()
  const router = useRouter()
  const userAuth = useUserAuth()
  const location = useLocation()

  const {
    expert: expertData,
    nextPage,
    loading: isPublicExpertsLoading,
    fetchNextPage,
  } = usePublicExpertDetailConsultationTab({
    slug,
  })

  const {
    selectedDate: selectedDateFromUrl,
    selectedSessionId: selectedSessionIdFromUrl,
    isGetNotifiedModalOpen: isGetNotifiedModalOpenFromUrl,
  } = queryString.parse(location.search)

  const [selectedSession, setSelectedSession] = useState<
    PublicExpertDetailConsultationTabQuery['expertBySlug']['sessions'][number] & {
      displayPrice?: CalculatedPrice
    }
  >(null)
  const [selectedDate, setSelectedDate] = useState(
    selectedDateFromUrl && !Array.isArray(selectedDateFromUrl)
      ? new Date(selectedDateFromUrl)
      : new Date(new Date().setUTCMinutes(0))
  )
  const [displayedDate, setDisplayedDate] = useState(selectedDate)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [bookingStep, setBookingStep] = useState(
    selectedDateFromUrl
      ? BOOKING_STEP.AUTHENTICATION
      : BOOKING_STEP.DATETIME_PICKER
  )
  const [getNotifiedStep, setGetNotifiedStep] = useState(
    GET_NOTIFIED_STEP.SUBMIT
  )
  const [isGetNotifiedModalOpen, setIsGetNotifiedModalOpen] = useState(false)
  const [expertRecommendationModal, setExpertRecommendationModal] = useState({
    visible: false,
    filters: {},
  })

  const {
    isPublicExpertAvailabilityLoading,
    isFirstPublicExpertAvailabilityLoading,
    firstAvailability,
    availabilities,
    availabilitiesInZonedTime,
  } = useBookingModalAvailabilitiesQuery({
    // @ts-ignore add proper type
    expertSession: selectedSession,
    displayedDate,
    selectedDate,
  })

  usePreselectAvailableTime({
    availabilities,
    bookingStep,
    firstAvailability,
    setDisplayedDate,
    setSelectedDate,
    isBookingModalOpen,
  })

  const [
    executeSubscribeToExpertAvailabilitiesMutation,
    { loading: isSubscribeToExpertAvailabilitiesLoading },
  ] = useClientSubscribeToExpertAvailabilitiesMutation()

  const [getPublicExperts] = useFeaturedExpertsLazyQuery()

  const isLoading =
    isFirstPublicExpertAvailabilityLoading || isPublicExpertAvailabilityLoading

  useExpertDetailParseDataFromUrlHook({
    availabilities,
    expertData,
    isGetNotifiedModalOpenFromUrl,
    isLoading,
    router,
    selectedDate,
    selectedDateFromUrl,
    selectedSessionIdFromUrl,
    setBookingStep,
    setGetNotifiedStep,
    setIsBookingModalOpen,
    setIsGetNotifiedModalOpen,
    setSelectedSession,
    userAuth,
  })

  const handleChangeLocation = useCallback(
    (nextUrl) => {
      if (isBookingModalOpen && !nextUrl.includes('session-checkout')) {
        trackAbandonedBookingCheckout()
      }
    },
    [isBookingModalOpen]
  )

  const handleLeavePage = useCallback(() => {
    if (isBookingModalOpen) {
      trackAbandonedBookingCheckout()
    }
  }, [isBookingModalOpen])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handleLeavePage, true)
      router.events.on('routeChangeStart', handleChangeLocation)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', handleLeavePage, true)
        router.events.off('routeChangeStart', handleChangeLocation)
      }
    }
  }, [handleChangeLocation, handleLeavePage, router.events])

  useEffect(() => {
    if (expertData && !eventFired.current) {
      executeDataToDataLayer({
        event: GA4_EVENTS.VIEW_ITEM,
        overrideData: {
          items: [expertData],
        },
      })

      executeDataToDataLayer({
        event: GA4_ECOM_CUSTOM_EVENTS.VIEW_ITEM_CONSULTATIONS,
        includeStoredData: false,
      })
      eventFired.current = true
    }
  }, [expertData, executeDataToDataLayer])

  const { MOBILE: isMobile, isReady } = useMedia()

  const resetBookingModal = () => {
    const resetDate = new Date(new Date().setUTCMinutes(0))
    setSelectedDate(resetDate)
    setDisplayedDate(resetDate)
    silentPushUrl({
      route: ROUTE.FIND_AN_EXPERT_CONSULTATION_TAB_DYNAMIC({
        slug: expertData.slug,
      }),
      router,
    })
  }

  const clearBookingModal = () => {
    setBookingStep(BOOKING_STEP.DATETIME_PICKER)
    setIsBookingModalOpen(false)
    setSelectedSession(null)
    resetBookingModal()
  }

  const closeGetNotifiedModal = () => {
    silentPushUrl({
      route: ROUTE.FIND_AN_EXPERT_CONSULTATION_TAB_DYNAMIC({
        slug: expertData.slug,
      }),
      router,
    })
    setIsGetNotifiedModalOpen(false)
  }

  const isUserImpersonifiedAdmin =
    userAuth.isAdminLoggedInAsClient || userAuth.isAdminLoggedInAsExpert

  return (
    <>
      <Grid
        mb={SPACE.PX_120}
        mt={SPACE.PX_40}
        px={CONTAINER_HORIZONTAL_PADDING}
        gridTemplateColumns={{
          // minmax is a workaround for issue with swiper width
          MOBILE: `minmax(0, 1fr)`,
          TABLET: `minmax(0, 1fr) ${ASIDE_MAX_WIDTH.TABLET}`,
          DESKTOP: `minmax(0, 1fr) ${ASIDE_MAX_WIDTH.DESKTOP}`,
        }}
        gap={SPACE.PX_20}
      >
        <Flex flexDirection="column" gap={SPACE.PX_64}>
          {expertData.longDescription && (
            <AboutSection description={expertData.longDescription} />
          )}
          <ExpertExpertises expertData={expertData} />
          {isMobile && isReady && (
            <SessionBookingCardListingMobile
              data={expertData}
              setGetNotifiedStep={setGetNotifiedStep}
              setIsBookingModalOpen={setIsBookingModalOpen}
              setIsGetNotifiedModalOpen={setIsGetNotifiedModalOpen}
              setSelectedSession={setSelectedSession}
            />
          )}
          <InfoCard
            heading="Why a 1:1 Consultation?"
            content={
              <>
                Whether your project is a gut renovation or a room in need of a
                refresh, {expertData.profileName} can guide you in creating a
                fully-realized space that you&apos;ll love living in.
                <br />
                <br />
                You can show your space over video, put together a presentation
                with photos and/or links to walk through over screen-share, or
                simply chat and take notes. The time is yours to use however is
                most helpful for you.
                <br />
                <br />
                The countless decisions of home design tend to be time-consuming
                and costly. Booking an Expert consultation makes the process
                easier &ndash; and more fun!
              </>
            }
          />
          {expertData.customCancelationPolicy && (
            <InfoCard
              heading="Cancellation Policy"
              content={expertData.customCancelationPolicy}
            />
          )}
          {expertData.testimonials.data.length > 0 && (
            <ExpertTestimonialsCarousel
              testimonials={expertData.testimonials.data}
            />
          )}
          {expertData.reviews.data.length > 0 && (
            <ExpertReviews
              reviewsAverageRating={expertData.reviewsAverageRating}
              nextPage={nextPage}
              reviews={expertData.reviews.data}
              isSeeMoreLoading={isPublicExpertsLoading}
              onSeeMore={() => {
                fetchNextPage()
              }}
            />
          )}
          <FAQSection />
          <ParticipateSection />
        </Flex>
        {!isMobile && isReady && (
          <aside>
            <SessionBookingCardListing
              data={expertData}
              setGetNotifiedStep={setGetNotifiedStep}
              setIsBookingModalOpen={setIsBookingModalOpen}
              setIsGetNotifiedModalOpen={setIsGetNotifiedModalOpen}
              setSelectedSession={setSelectedSession}
              position="sticky"
              height="max-content"
              top={
                isUserImpersonifiedAdmin
                  ? `calc(${HEADER_HEIGHT_DESKTOP} + ${SIGNED_IN_AS_USER_HEADER_DESKTOP_HEIGHT} + ${SPACE.PX_20})`
                  : `calc(${HEADER_HEIGHT_DESKTOP} + ${SPACE.PX_20})`
              }
            />
          </aside>
        )}
      </Grid>
      {expertRecommendationModal.visible && (
        <RecommendExpertModal
          isModalOpen
          filters={expertRecommendationModal.filters}
          onModalClose={() => {
            setExpertRecommendationModal({ visible: false, filters: {} })
          }}
        />
      )}

      {isGetNotifiedModalOpen && (
        <GetNotifiedModal
          isLoginLoading={userAuth?.isPageLoading}
          isLoading={isSubscribeToExpertAvailabilitiesLoading}
          step={getNotifiedStep}
          isModalOpen={isGetNotifiedModalOpen}
          onModalClose={closeGetNotifiedModal}
          onConfirmModal={async ({
            email,
            name,
            description,
            dateRange,
            timeRange,
            timezone,
          }) => {
            const formattedEmail = email && email.trim()
            const response =
              await executeSubscribeToExpertAvailabilitiesMutation({
                // Signed-in subscribers are resolved from the token; anonymous
                // ones from the email arg — only the latter goes tokenless.
                context: { isPublic: !userAuth?.isClientSignedIn },
                variables: {
                  input: {
                    metadata: {
                      name,
                      description,
                      dateRange,
                      timeRange,
                      timezone,
                    },
                    expertId: expertData.id,
                    ...(formattedEmail && !userAuth?.isClientSignedIn
                      ? { email: formattedEmail }
                      : {}),
                    ...(selectedSession
                      ? {
                          expertSessionId: selectedSession.id,
                        }
                      : {}),
                  },
                },
              })

            let recommendFilters: {
              price?: {
                from: number
                to: number
              }
              availability?: { from: string; to: string }
              sessionLength?: number[]
            } = {}
            if (selectedSession) {
              const fullFilters = getSimilarSessionFilters({
                dateRange,
                selectedSession,
                expertData,
              })
              const partialFilters = getSimilarSessionFilters({
                dateRange,
                selectedSession,
                expertData,
                applyLength: false,
                applyPrice: false,
                applyTags: false,
              })

              const [hasFullMatch, hasPartialMatch] = await Promise.all([
                getPublicExperts({
                  variables: {
                    expertsInput: {
                      filter: fullFilters,
                    },
                  },
                }).then(hasResponseSomeResults),
                getPublicExperts({
                  variables: {
                    expertsInput: {
                      filter: partialFilters,
                    },
                  },
                }).then(hasResponseSomeResults),
              ])

              if (hasFullMatch) {
                recommendFilters = fullFilters
              } else if (hasPartialMatch) {
                recommendFilters = partialFilters
              }
            }

            if (!response.errors) {
              setExpertRecommendationModal({
                visible: true,
                filters: recommendFilters,
              })
              klaviyoIdentifyByEmail(formattedEmail)
              closeGetNotifiedModal()
              executeDataToDataLayer({
                event: GA4_EVENTS.NOTIFY_ME,
              })
            }
          }}
        />
      )}
      {selectedSession && isBookingModalOpen && (
        /* @ts-expect-error - Component needs to be TS-ified */
        <BookingModal
          step={bookingStep}
          setStep={(newBookingStep, date) => {
            if (
              bookingStep === BOOKING_STEP.AVAILABILITY_ERROR &&
              newBookingStep === BOOKING_STEP.DATETIME_PICKER
            ) {
              resetBookingModal()
            }
            if (
              bookingStep === BOOKING_STEP.DATETIME_PICKER &&
              newBookingStep === BOOKING_STEP.AUTHENTICATION
            ) {
              silentPushUrl({
                router,
                route: ROUTE.FIND_AN_EXPERT_CONSULTATION_TAB_DYNAMIC({
                  slug: expertData.slug,
                }),
                params: {
                  selectedDate: date.toISOString(),
                  selectedSessionId: selectedSession.id,
                  fromUrl: GA4_SIGN_IN_AREAS.CONSULTATION_CALENDAR,
                },
              })
            }
            if (bookingStep === BOOKING_STEP.AUTHENTICATION) {
              silentPushUrl({
                route: ROUTE.FIND_AN_EXPERT_CONSULTATION_TAB_DYNAMIC({
                  slug: expertData.slug,
                }),
                router,
              })
            }
            setBookingStep(newBookingStep)
          }}
          isModalOpen={isBookingModalOpen}
          onModalClose={() => {
            trackAbandonedBookingCheckout()
            clearBookingModal()
          }}
          selectedDate={userAuth.convertDateToTimezone(selectedDate)}
          onChangeSelectedDate={(newDate) => {
            setSelectedDate(userAuth.convertZonedDateToUTC(newDate))
          }}
          isAvailabilityLoading={isLoading}
          isLoginLoading={userAuth?.isPageLoading}
          activeDates={availabilitiesInZonedTime}
          session={selectedSession}
          displayedDate={userAuth.convertDateToTimezone(displayedDate)}
          onChangeDisplayedDate={(newDate) => {
            setDisplayedDate(userAuth.convertZonedDateToUTC(newDate))
          }}
        />
      )}
    </>
  )
}
